import { Stack, Typography } from '@mui/material';

import { useSelector } from 'react-redux';

import { ReactComponent as DownloadIcon } from 'common/assets/images/download.svg';
import { ReactComponent as ShareIcon } from 'common/assets/images/share-icon.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import { MODULES } from 'common/constants';
import {
	AI_SIMULATION_GENAI_DISCLAIMER,
	AI_SIMULATION_TYPES,
} from 'common/constants/AISimulationConstants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { RootState } from 'common/store/AppStore';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { useFeatureFlag } from 'hooks/useFeatureFlags';

import { mapTitleWithModel } from 'features/AiSimulation/AiSimulationUtil';
import ControlTestScriptStepsLayout from 'features/TestMonitoring/TestScriptHubLandingPage/ControlTestSteps/ControlTestScriptStepsLayout';

import { IControlTestScriptSectionProps } from '../api/type-def';
import generatedTextAreaStyles from '../Components/GeneratedTextArea/GeneratedTextArea.module.scss';

import AIFeedback from '../../../common/components/AIFeedback/AIFeedback';

const ControlTestScriptOutputLayout = (
	props: IControlTestScriptSectionProps,
) => {
	const {
		inputData,
		outputResult,
		testScriptSteps,
		outputActionClassName,
		downloadHandler,
		isGenerating,
		shareHandler,
	} = props;
	const { IS_EXPORT_BUTTON_ENABLED } = FeatureFlagsKeysEnum;
	const { DISCLAIMER } = AI_SIMULATION_GENAI_DISCLAIMER;

	const isLDResponseExists: boolean = useSelector(
		(state: RootState) => state.common.isLDResponseAvailable,
	);
	const isExportButtonEnabled = useFeatureFlag(
		IS_EXPORT_BUTTON_ENABLED,
		isLDResponseExists,
	);

	const selectedAIModel = useSelector(
		(state: RootState) => state?.simulationArena?.selectedAIModel,
	);
	const selectedAIRecipe = useSelector(
		(state: RootState) => state.simulationArena.selectedAIRecipe,
	);

	const { AI_SIMULATION_AND_EXPERIMENTATION } = MODULES;

	const title = AI_SIMULATION_TYPES.CONTROL_TEST_SCRIPTS;
	const shouldReset = isGenerating || false;

	const getControlRefCondition = () =>
		selectedAIModel?.id || selectedAIRecipe?.id;
	return (
		<Stack gap={2} className={generatedTextAreaStyles['section-container']}>
			<Stack direction="row" justifyContent="space-between">
				<Typography className={generatedTextAreaStyles.title}>
					{toSentenceCase('Control Test Script')}
				</Typography>
				<Stack direction="row" gap={1}>
					<ButtonComp
						btnType="secondary"
						data-testid="download-icon"
						size="small"
						disabled={isExportButtonEnabled || !outputResult}
						clickHandler={downloadHandler}
					>
						<DownloadIcon
							className={
								isExportButtonEnabled
									? generatedTextAreaStyles.disabled
									: `${outputActionClassName} ${generatedTextAreaStyles['download-icon']}`
							}
						/>{' '}
						Download
					</ButtonComp>
					<ButtonComp
						btnType="secondary"
						data-testid="share-btn"
						size="small"
						clickHandler={shareHandler}
						disabled={!outputResult}
					>
						<ShareIcon
							className={`${outputActionClassName} ${generatedTextAreaStyles['activity-icon-color']}`}
						/>{' '}
						Share
					</ButtonComp>
				</Stack>
			</Stack>
			<Stack>
				<ControlTestScriptStepsLayout
					type=""
					isActionsEnabled
					controValue=""
					generatedSteps={testScriptSteps}
					loading={false}
					deleteModal
				/>
			</Stack>
			<Stack direction="row" justifyContent="space-between">
				<Typography>{DISCLAIMER}</Typography>

				<AIFeedback
					objectType={mapTitleWithModel[title]}
					module={AI_SIMULATION_AND_EXPERIMENTATION}
					objectData={{
						prompt: inputData,
						response: outputResult,
						reference: getControlRefCondition(),
					}}
					isDisabled={!outputResult}
					shouldResetFeedback={shouldReset}
					allowSaveObjectType
				/>
			</Stack>
		</Stack>
	);
};
export default ControlTestScriptOutputLayout;
